import { useEffect } from "react";
import type { Editor } from "@tiptap/react";
import type { EditorView } from "@codemirror/view";
import { TOC_SELECTOR } from "@/components/editor/useTocNavigation";

type ScrollInitiator = "tiptap" | "cm" | "toc" | null;

function makeScrollInitiatorSetter(
  scrollInitiatorRef: React.RefObject<ScrollInitiator>,
  scrollInitiatorTimerRef: React.RefObject<ReturnType<typeof setTimeout> | undefined>,
) {
  return (initiator: "tiptap" | "cm") => {
    scrollInitiatorRef.current = initiator;
    clearTimeout(scrollInitiatorTimerRef.current);
    scrollInitiatorTimerRef.current = setTimeout(() => {
      scrollInitiatorRef.current = null;
    }, 300);
  };
}

function syncReportPane(
  childIdx: number,
  reportPaneRef: React.RefObject<HTMLDivElement | null>,
  reportLineMapRef: React.RefObject<number[]>,
) {
  const pane = reportPaneRef.current;
  if (!pane) {
    return;
  }
  const rlm = reportLineMapRef.current;
  const lineIdx = childIdx < rlm.length ? (rlm[childIdx] ?? -1) : -1;
  if (lineIdx === -1) {
    return;
  }
  const lines = pane.querySelectorAll<HTMLElement>(".report-line");
  const el = lines[lineIdx];
  if (el) {
    pane.scrollTop += el.getBoundingClientRect().top - pane.getBoundingClientRect().top;
  }
}

function updateTocHeading(
  editor: Editor,
  wrapperRect: DOMRect,
  setScrollInitiator: (i: "tiptap" | "cm") => void,
  setActiveHeadingIndex: (idx: number) => void,
) {
  const headings = [...editor.view.dom.querySelectorAll(TOC_SELECTOR)];
  for (const [i, heading] of headings.entries()) {
    const rect = heading.getBoundingClientRect();
    if (rect.top >= wrapperRect.top && rect.top < wrapperRect.bottom) {
      setScrollInitiator("tiptap");
      setActiveHeadingIndex(i);
      break;
    }
  }
}

function syncCmToTiptap(
  childIdx: number,
  cmViewRef: React.RefObject<EditorView | null>,
  lineMapRef: React.RefObject<number[]>,
) {
  const cmView = cmViewRef.current;
  if (!cmView || childIdx === -1) {
    return;
  }
  const lm = lineMapRef.current;
  if (childIdx >= lm.length) {
    return;
  }
  const sourceLine = lm[childIdx] ?? 1;
  try {
    const lineFrom = cmView.state.doc.line(sourceLine).from;
    cmView.scrollDOM.scrollTop = cmView.lineBlockAt(lineFrom).top;
  } catch {
    /* ignore */
  }
}

function findFirstVisibleChild(editor: Editor, wrapperTop: number): number {
  const children = [...editor.view.dom.children] as HTMLElement[];
  for (const [i, child] of children.entries()) {
    if (child.getBoundingClientRect().bottom > wrapperTop) {
      return i;
    }
  }
  return -1;
}

function binarySearchLineMap(lm: number[], targetLine: number): number {
  let lo = 0,
    hi = lm.length - 1,
    nodeIdx = 0;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if ((lm[mid] ?? Infinity) <= targetLine) {
      nodeIdx = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return nodeIdx;
}

function getFirstVisibleCmLine(view: EditorView): number | null {
  try {
    return view.state.doc.lineAt(view.lineBlockAtHeight(view.scrollDOM.scrollTop + 1).from).number;
  } catch {
    return null;
  }
}

function scrollWrapperToChild(wrapper: HTMLDivElement, editor: Editor, nodeIdx: number) {
  const children = [...editor.view.dom.children] as HTMLElement[];
  const el = children[nodeIdx] as HTMLElement | undefined;
  if (!el) {
    return;
  }
  try {
    const wrapperRect = wrapper.getBoundingClientRect();
    wrapper.scrollTop = el.getBoundingClientRect().top - wrapperRect.top + wrapper.scrollTop;
  } catch {
    /* ignore */
  }
}

interface ScrollSyncRefs {
  editorRef: React.RefObject<Editor | null>;
  cmViewRef: React.RefObject<EditorView | null>;
  lineMapRef: React.RefObject<number[]>;
  reportLineMapRef: React.RefObject<number[]>;
  reportPaneRef: React.RefObject<HTMLDivElement | null>;
  scrollInitiatorRef: React.RefObject<ScrollInitiator>;
  scrollInitiatorTimerRef: React.RefObject<ReturnType<typeof setTimeout> | undefined>;
  tiptapEditingRef: React.RefObject<boolean>;
}

function useTiptapScrollSpy(
  editorWrapperEl: HTMLDivElement | null,
  showSource: boolean,
  showReport: boolean,
  refs: ScrollSyncRefs,
  setScrollInitiator: (i: "tiptap" | "cm") => void,
  setActiveHeadingIndex: (idx: number) => void,
) {
  useEffect(() => {
    if (!editorWrapperEl) {
      return;
    }
    let rafId: number;
    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const editor = refs.editorRef.current;
        if (!editor) {
          return;
        }
        const wrapperRect = editorWrapperEl.getBoundingClientRect();
        const childIdx = findFirstVisibleChild(editor, wrapperRect.top);
        if (showReport && childIdx !== -1) {
          syncReportPane(childIdx, refs.reportPaneRef, refs.reportLineMapRef);
        }
        if (refs.scrollInitiatorRef.current === "cm") {
          return;
        }
        if (refs.scrollInitiatorRef.current !== "toc") {
          updateTocHeading(editor, wrapperRect, setScrollInitiator, setActiveHeadingIndex);
        }
        if (showSource && !refs.tiptapEditingRef.current) {
          syncCmToTiptap(childIdx, refs.cmViewRef, refs.lineMapRef);
        }
      });
    };
    editorWrapperEl.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      editorWrapperEl.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(rafId);
    };
  }, [editorWrapperEl, showSource, showReport]); // eslint-disable-line react-hooks/exhaustive-deps
}

function useCollapsedSectionSync(
  editorWrapperEl: HTMLDivElement | null,
  collapsedSections: Record<string, boolean>,
) {
  useEffect(() => {
    if (!editorWrapperEl) {
      return;
    }
    let id2: number;
    const id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => {
        editorWrapperEl.dispatchEvent(new Event("scroll"));
      });
    });
    return () => {
      cancelAnimationFrame(id1);
      cancelAnimationFrame(id2);
    };
  }, [collapsedSections, editorWrapperEl]);
}

function useCmToTiptapSync(
  editorWrapperEl: HTMLDivElement | null,
  showSource: boolean,
  refs: ScrollSyncRefs,
  setScrollInitiator: (i: "tiptap" | "cm") => void,
) {
  useEffect(() => {
    if (!showSource) {
      return;
    }
    const view = refs.cmViewRef.current;
    if (!view) {
      return;
    }
    let rafId: number;
    const onCmScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        if (
          refs.scrollInitiatorRef.current === "tiptap" ||
          refs.scrollInitiatorRef.current === "toc"
        ) {
          return;
        }
        const editor = refs.editorRef.current;
        const wrapper = editorWrapperEl;
        if (!editor || !wrapper) {
          return;
        }
        const firstVisibleLine = getFirstVisibleCmLine(view);
        if (firstVisibleLine === null) {
          return;
        }
        const lm = refs.lineMapRef.current;
        if (lm.length === 0) {
          return;
        }
        const nodeIdx = binarySearchLineMap(lm, firstVisibleLine);
        setScrollInitiator("cm");
        scrollWrapperToChild(wrapper, editor, nodeIdx);
      });
    };
    view.scrollDOM.addEventListener("scroll", onCmScroll, { passive: true });
    return () => {
      view.scrollDOM.removeEventListener("scroll", onCmScroll);
      cancelAnimationFrame(rafId);
    };
  }, [showSource, editorWrapperEl]); // eslint-disable-line react-hooks/exhaustive-deps
}

export function useScrollSync(
  editorWrapperEl: HTMLDivElement | null,
  editorRef: React.RefObject<Editor | null>,
  showSource: boolean,
  showReport: boolean,
  cmViewRef: React.RefObject<EditorView | null>,
  lineMapRef: React.RefObject<number[]>,
  reportLineMapRef: React.RefObject<number[]>,
  reportPaneRef: React.RefObject<HTMLDivElement | null>,
  scrollInitiatorRef: React.RefObject<ScrollInitiator>,
  scrollInitiatorTimerRef: React.RefObject<ReturnType<typeof setTimeout> | undefined>,
  tiptapEditingRef: React.RefObject<boolean>,
  collapsedSections: Record<string, boolean>,
  setActiveHeadingIndex: (idx: number) => void,
) {
  const setScrollInitiator = makeScrollInitiatorSetter(scrollInitiatorRef, scrollInitiatorTimerRef);
  const refs: ScrollSyncRefs = {
    cmViewRef,
    editorRef,
    lineMapRef,
    reportLineMapRef,
    reportPaneRef,
    scrollInitiatorRef,
    scrollInitiatorTimerRef,
    tiptapEditingRef,
  };

  useTiptapScrollSpy(
    editorWrapperEl,
    showSource,
    showReport,
    refs,
    setScrollInitiator,
    setActiveHeadingIndex,
  );
  useCollapsedSectionSync(editorWrapperEl, collapsedSections);
  useCmToTiptapSync(editorWrapperEl, showSource, refs, setScrollInitiator);
}
